import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Boxes, Plus, RefreshCw, Copy, Check, ExternalLink, Send, Archive, Trash2,
    Search, X, Filter, Sparkles, ArrowRight, AlertCircle, Link2, Clock,
} from "lucide-react";
import { toast } from "sonner";
import {
    fetchProjects,
    createProject,
    updateProject,
    deleteProject,
} from "@/lib/adminApi";

const STATUS_FILTERS = [
    { value: "all", label: "All" },
    { value: "pending", label: "Pending" },
    { value: "delivered", label: "Delivered" },
    { value: "archived", label: "Archived" },
];

const PROJECT_TYPES = ["Branding", "Video / Motion", "Logo Design", "Brand Identity System", "Brand Foundation Pack", "Other"];

const emptyProject = () => ({
    client_name: "",
    business_name: "",
    project_name: "",
    project_type: "Branding",
    drive_link: "",
    delivery_message: "Your project is ready. Click the button below to access your files.",
    notes: "",
});

export default function AdminProjects() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState("all");
    const [search, setSearch] = useState("");
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editing, setEditing] = useState(null); // project under edit
    const [form, setForm] = useState(() => emptyProject());
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [copiedCode, setCopiedCode] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        const params = {};
        if (filterStatus !== "all") params.status = filterStatus;
        try {
            const res = await fetchProjects(params);
            setItems(res.items || []);
        } catch (_err) {
            toast.error("Could not load projects");
        } finally {
            setLoading(false);
        }
    }, [filterStatus]);

    useEffect(() => { load(); }, [load]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return items;
        return items.filter((p) =>
            [p.client_name, p.business_name, p.project_name, p.code, p.project_type]
                .filter(Boolean)
                .some((s) => String(s).toLowerCase().includes(q))
        );
    }, [items, search]);

    const stats = useMemo(() => {
        const all = items;
        return {
            total: all.length,
            pending: all.filter((p) => p.status === "pending").length,
            delivered: all.filter((p) => p.status === "delivered").length,
            archived: all.filter((p) => p.status === "archived").length,
        };
    }, [items]);

    const openCreate = () => {
        setEditing(null);
        setForm(emptyProject());
        setErrors({});
        setDrawerOpen(true);
    };
    const openEdit = (p) => {
        setEditing(p);
        setForm({
            client_name: p.client_name || "",
            business_name: p.business_name || "",
            project_name: p.project_name || "",
            project_type: p.project_type || "Branding",
            drive_link: p.drive_link || "",
            delivery_message: p.delivery_message || "",
            notes: p.notes || "",
        });
        setErrors({});
        setDrawerOpen(true);
    };

    const closeDrawer = () => setDrawerOpen(false);

    const validateForm = () => {
        const e = {};
        if (!form.client_name.trim()) e.client_name = "Client name is required.";
        if (!form.project_name.trim()) e.project_name = "Project name is required.";
        if (!form.drive_link.trim()) e.drive_link = "Google Drive link is required.";
        else if (!/^https?:\/\//.test(form.drive_link.trim())) e.drive_link = "Use a full URL starting with https://";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const submit = async (ev) => {
        ev?.preventDefault?.();
        if (!validateForm()) return;
        setSubmitting(true);
        try {
            if (editing) {
                const updated = await updateProject(editing.id, form);
                setItems((arr) => arr.map((p) => (p.id === editing.id ? updated : p)));
                toast.success("Project updated");
            } else {
                const created = await createProject(form);
                setItems((arr) => [created, ...arr]);
                toast.success("Project created", { description: `Access code: ${created.code}` });
            }
            setDrawerOpen(false);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
            toast.error("Could not save", { description: msg || "Please try again." });
        } finally {
            setSubmitting(false);
        }
    };

    const markDelivered = async (p) => {
        try {
            const updated = await updateProject(p.id, { status: "delivered" });
            setItems((arr) => arr.map((x) => (x.id === p.id ? updated : x)));
            toast.success("Marked delivered", { description: p.code });
        } catch (_err) {
            toast.error("Update failed");
        }
    };
    const markArchived = async (p) => {
        try {
            const updated = await updateProject(p.id, { status: "archived" });
            setItems((arr) => arr.map((x) => (x.id === p.id ? updated : x)));
            toast.success("Archived");
        } catch (_err) {
            toast.error("Update failed");
        }
    };
    const remove = async (p) => {
        if (!window.confirm(`Delete project ${p.code}? This cannot be undone.`)) return;
        try {
            await deleteProject(p.id);
            setItems((arr) => arr.filter((x) => x.id !== p.id));
            toast.success("Project deleted");
        } catch (_err) {
            toast.error("Delete failed");
        }
    };

    const copyCode = async (code) => {
        try {
            await navigator.clipboard.writeText(code);
            setCopiedCode(code);
            toast.success("Code copied", { description: code });
            setTimeout(() => setCopiedCode(""), 1800);
        } catch (_err) {
            toast.error("Could not copy");
        }
    };

    const copyDeliveryLink = async (code) => {
        try {
            const origin = typeof window !== "undefined" ? window.location.origin : "";
            const link = `${origin}/delivery?code=${encodeURIComponent(code)}`;
            await navigator.clipboard.writeText(link);
            toast.success("Delivery link copied", {
                description: link.length > 60 ? `${link.slice(0, 60)}\u2026` : link,
            });
        } catch (_err) {
            toast.error("Could not copy link");
        }
    };

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    return (
        <div className="px-5 py-8 sm:px-8 sm:py-10 lg:px-10">
            {/* Header */}
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <span className="pg-eyebrow">Studio CMS · Client Deliveries</span>
                    <h1
                        className="font-display mt-4 text-3xl sm:text-4xl"
                        style={{ color: "var(--pg-fg)" }}
                        data-testid="admin-projects-headline"
                    >
                        Deliveries
                    </h1>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        type="button"
                        onClick={load}
                        data-testid="admin-projects-refresh"
                        className="pg-btn-ghost pg-app-tap"
                    >
                        <RefreshCw className="h-4 w-4" /> Refresh
                    </button>
                    <button
                        type="button"
                        onClick={openCreate}
                        data-testid="admin-projects-new"
                        className="pg-btn-primary pg-app-tap"
                    >
                        <Plus className="h-4 w-4" /> New Delivery
                    </button>
                </div>
            </div>

            {/* Stats */}
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <StatCard label="Total" value={stats.total} />
                <StatCard label="Pending" value={stats.pending} accent />
                <StatCard label="Delivered" value={stats.delivered} />
                <StatCard label="Archived" value={stats.archived} />
            </div>

            {/* Filters */}
            <div className="mt-7 flex flex-wrap items-center gap-3">
                <Filter className="h-4 w-4" style={{ color: "var(--pg-fg-muted)" }} />
                <div className="inline-flex gap-1 rounded-full border p-1" style={{ borderColor: "var(--pg-border)" }}>
                    {STATUS_FILTERS.map((o) => {
                        const active = filterStatus === o.value;
                        return (
                            <button
                                key={o.value}
                                type="button"
                                onClick={() => setFilterStatus(o.value)}
                                data-testid={`admin-projects-filter-${o.value}`}
                                className="rounded-full px-3 py-1 text-xs font-mono uppercase tracking-[0.16em] transition-colors"
                                style={{
                                    background: active ? "var(--pg-fg)" : "transparent",
                                    color: active ? "var(--pg-bg)" : "var(--pg-fg-muted)",
                                }}
                            >
                                {o.label}
                            </button>
                        );
                    })}
                </div>
                <div className="relative ml-auto w-full max-w-xs">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" style={{ color: "var(--pg-fg-muted)" }} />
                    <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search code, client, project…"
                        data-testid="admin-projects-search"
                        className="pg-input pl-9"
                    />
                </div>
            </div>

            {/* Cards (mobile + desktop responsive grid) */}
            <div className="mt-6">
                {loading ? (
                    <div className="flex items-center justify-center rounded-2xl border px-6 py-20 text-sm" style={{ borderColor: "var(--pg-border)", color: "var(--pg-fg-muted)" }}>
                        Loading…
                    </div>
                ) : filtered.length === 0 ? (
                    <div
                        className="flex flex-col items-center rounded-2xl border px-6 py-20 text-center text-sm"
                        style={{ borderColor: "var(--pg-border)", color: "var(--pg-fg-muted)", background: "var(--pg-surface)" }}
                        data-testid="admin-projects-empty"
                    >
                        <Boxes className="mb-3 h-6 w-6" />
                        No deliveries yet. Create a new delivery to generate an access code for your client.
                        <button onClick={openCreate} className="pg-btn-primary pg-app-tap mt-5">
                            <Plus className="h-4 w-4" /> Create First Delivery
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="admin-projects-grid">
                        {filtered.map((p) => (
                            <ProjectCard
                                key={p.id}
                                project={p}
                                copied={copiedCode === p.code}
                                onCopy={() => copyCode(p.code)}
                                onCopyLink={() => copyDeliveryLink(p.code)}
                                onEdit={() => openEdit(p)}
                                onDeliver={() => markDelivered(p)}
                                onArchive={() => markArchived(p)}
                                onDelete={() => remove(p)}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Create/Edit Drawer */}
            <AnimatePresence>
                {drawerOpen && (
                    <FormDrawer
                        editing={editing}
                        form={form}
                        errors={errors}
                        submitting={submitting}
                        onChange={setField}
                        onSubmit={submit}
                        onClose={closeDrawer}
                    />
                )}
            </AnimatePresence>
        </div>
    );
}

const StatCard = ({ label, value, accent = false }) => (
    <div
        className="pg-app-surface p-5"
    >
        <div
            className={`font-display text-2xl sm:text-3xl ${accent ? "pg-text-gold-static" : ""}`}
            style={!accent ? { color: "var(--pg-fg)" } : undefined}
        >
            {value}
        </div>
        <div className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--pg-fg-muted)" }}>
            {label}
        </div>
    </div>
);

const StatusPill = ({ status }) => {
    const map = {
        pending: { bg: "rgba(246,199,137,0.18)", fg: "#8C5E1E", label: "Pending" },
        delivered: { bg: "rgba(34,197,94,0.18)", fg: "#16A34A", label: "Delivered" },
        archived: { bg: "rgba(120,120,120,0.18)", fg: "var(--pg-fg-muted)", label: "Archived" },
    };
    const c = map[status] || map.pending;
    return (
        <span
            className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em]"
            style={{ background: c.bg, color: c.fg }}
        >
            {c.label}
        </span>
    );
};

const ProjectCard = ({ project, copied, onCopy, onCopyLink, onEdit, onDeliver, onArchive, onDelete }) => {
    const created = project.created_at ? new Date(project.created_at).toLocaleDateString() : "";
    const deliveredRelative = (() => {
        if (!project.delivered_at) return null;
        const ms = Date.now() - new Date(project.delivered_at).getTime();
        const d = Math.floor(ms / (1000 * 60 * 60 * 24));
        if (d <= 0) {
            const h = Math.floor(ms / (1000 * 60 * 60));
            if (h <= 0) return "just now";
            return `${h}h ago`;
        }
        if (d === 1) return "yesterday";
        if (d < 30) return `${d}d ago`;
        const m = Math.floor(d / 30);
        return `${m}mo ago`;
    })();
    return (
        <motion.article
            layout
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            whileHover={{ y: -3 }}
            className="pg-app-surface flex flex-col gap-4 p-5"
            data-testid="admin-project-card"
            data-project-id={project.id}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="font-display text-lg leading-tight truncate" style={{ color: "var(--pg-fg)" }}>{project.project_name}</div>
                    <div className="mt-0.5 text-xs truncate" style={{ color: "var(--pg-fg-muted)" }}>
                        {project.client_name}{project.business_name ? ` · ${project.business_name}` : ""}
                    </div>
                </div>
                <StatusPill status={project.status} />
            </div>

            <button
                type="button"
                onClick={onCopy}
                data-testid="admin-project-copy-code"
                className="pg-app-tap group flex items-center justify-between gap-2 rounded-xl border px-3 py-2.5 text-left transition-colors"
                style={{
                    borderColor: "color-mix(in srgb, var(--pg-accent) 35%, var(--pg-border))",
                    background: "color-mix(in srgb, var(--pg-accent) 8%, transparent)",
                }}
            >
                <span className="font-mono text-sm tracking-[0.18em]" style={{ color: "var(--pg-fg)" }}>{project.code}</span>
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg" style={{ background: "var(--pg-surface-2)", color: "var(--pg-fg-muted)" }}>
                    {copied ? <Check className="h-3.5 w-3.5" style={{ color: "var(--pg-accent)" }} /> : <Copy className="h-3.5 w-3.5" />}
                </span>
            </button>

            <button
                type="button"
                onClick={onCopyLink}
                data-testid="admin-project-copy-link"
                className="pg-app-tap inline-flex items-center gap-2 self-start rounded-md border px-2.5 py-1.5 text-[11px] font-mono uppercase tracking-[0.16em] transition-colors hover:bg-[var(--pg-surface-2)]"
                style={{ borderColor: "var(--pg-border)", color: "var(--pg-fg-muted)" }}
                title="Copy the full delivery URL to share with your client"
            >
                <Link2 className="h-3 w-3" /> Copy Delivery Link
            </button>

            <div className="flex flex-wrap items-center gap-2 text-[11px]" style={{ color: "var(--pg-fg-muted)" }}>
                <span className="font-mono uppercase tracking-[0.18em]">{project.project_type}</span>
                {created && <span>\u00b7 {created}</span>}
                {deliveredRelative && (
                    <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5" style={{ borderColor: "var(--pg-border-strong)", background: "color-mix(in srgb, var(--pg-accent) 12%, transparent)" }}>
                        <Clock className="h-3 w-3" /> Delivered {deliveredRelative}
                    </span>
                )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
                <a
                    href={project.drive_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="pg-app-tap pg-btn-ghost"
                    data-testid="admin-project-open-drive"
                >
                    <ExternalLink className="h-4 w-4" /> Drive
                </a>
                {project.status !== "delivered" && (
                    <button
                        type="button"
                        onClick={onDeliver}
                        className="pg-app-tap pg-btn-primary"
                        data-testid="admin-project-mark-delivered"
                    >
                        <Send className="h-4 w-4" /> Mark Delivered
                    </button>
                )}
                <button
                    type="button"
                    onClick={onEdit}
                    className="pg-app-tap inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-mono uppercase tracking-[0.18em]"
                    style={{ borderColor: "var(--pg-border)", color: "var(--pg-fg-muted)" }}
                    data-testid="admin-project-edit"
                >
                    Edit
                </button>
                {project.status !== "archived" && (
                    <button
                        type="button"
                        onClick={onArchive}
                        className="pg-app-tap inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-mono uppercase tracking-[0.18em]"
                        style={{ borderColor: "var(--pg-border)", color: "var(--pg-fg-muted)" }}
                        data-testid="admin-project-archive"
                    >
                        <Archive className="h-3 w-3" /> Archive
                    </button>
                )}
                <button
                    type="button"
                    onClick={onDelete}
                    aria-label="Delete project"
                    className="pg-app-tap inline-flex items-center justify-center rounded-full border h-8 w-8 ml-auto"
                    style={{ borderColor: "var(--pg-border)", color: "#E55353" }}
                    data-testid="admin-project-delete"
                >
                    <Trash2 className="h-3.5 w-3.5" />
                </button>
            </div>
        </motion.article>
    );
};

const FormDrawer = ({ editing, form, errors, submitting, onChange, onSubmit, onClose }) => {
    return (
        <motion.div
            className="fixed inset-0 z-50 flex items-end justify-end bg-black/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            data-testid="admin-project-form-drawer"
        >
            <motion.aside
                onClick={(e) => e.stopPropagation()}
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                className="flex h-full w-full flex-col overflow-y-auto sm:w-[520px]"
                style={{ background: "var(--pg-bg)", borderLeft: "1px solid var(--pg-border)" }}
            >
                <div className="flex items-start justify-between gap-3 border-b px-5 py-5 sm:px-7 sm:py-6" style={{ borderColor: "var(--pg-border)" }}>
                    <div>
                        <div className="flex items-center gap-2">
                            <Sparkles className="h-4 w-4" style={{ color: "var(--pg-accent)" }} />
                            <span className="font-mono text-[11px] uppercase tracking-[0.22em]" style={{ color: "var(--pg-fg-muted)" }}>
                                {editing ? "Edit Delivery" : "New Delivery"}
                            </span>
                        </div>
                        <h2 className="font-display mt-3 text-2xl sm:text-3xl" style={{ color: "var(--pg-fg)" }}>
                            {editing ? editing.code : "Create a delivery"}
                        </h2>
                        <p className="mt-2 text-sm" style={{ color: "var(--pg-fg-muted)" }}>
                            {editing
                                ? "Update the delivery details. The access code stays the same."
                                : "Generates a unique access code your client can redeem on /delivery."}
                        </p>
                    </div>
                    <button onClick={onClose} aria-label="Close" className="rounded-full p-2" style={{ color: "var(--pg-fg-muted)" }}>
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <form onSubmit={onSubmit} className="flex flex-1 flex-col gap-5 px-5 py-6 sm:px-7" data-testid="admin-project-form">
                    <DrawerField id="client_name" label="Client Name" required value={form.client_name} onChange={(v) => onChange("client_name", v)} placeholder="e.g. Jordan Lee" error={errors.client_name} testId="admin-project-client-name" />
                    <DrawerField id="business_name" label="Business Name" value={form.business_name} onChange={(v) => onChange("business_name", v)} placeholder="e.g. Lee Roofing Co." testId="admin-project-business-name" />
                    <DrawerField id="project_name" label="Project Name" required value={form.project_name} onChange={(v) => onChange("project_name", v)} placeholder="e.g. Brand Foundation Pack Delivery" error={errors.project_name} testId="admin-project-name" />

                    <div className="flex flex-col gap-1.5">
                        <DrawerLabel>Project Type</DrawerLabel>
                        <select
                            value={form.project_type}
                            onChange={(e) => onChange("project_type", e.target.value)}
                            className="pg-input"
                            data-testid="admin-project-type"
                        >
                            {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>

                    <DrawerField id="drive_link" label="Google Drive Link" required value={form.drive_link} onChange={(v) => onChange("drive_link", v)} placeholder="https://drive.google.com/..." error={errors.drive_link} testId="admin-project-drive-link" />

                    <div className="flex flex-col gap-1.5">
                        <DrawerLabel>Delivery Message</DrawerLabel>
                        <textarea
                            value={form.delivery_message}
                            onChange={(e) => onChange("delivery_message", e.target.value)}
                            rows={3}
                            data-testid="admin-project-delivery-message"
                            className="pg-input"
                            style={{ resize: "vertical", minHeight: 100 }}
                        />
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <DrawerLabel>Internal Notes (private)</DrawerLabel>
                        <textarea
                            value={form.notes}
                            onChange={(e) => onChange("notes", e.target.value)}
                            rows={2}
                            data-testid="admin-project-notes"
                            className="pg-input"
                            style={{ resize: "vertical", minHeight: 80 }}
                        />
                    </div>

                    <div className="sticky bottom-0 -mx-5 mt-auto border-t px-5 py-4 sm:-mx-7 sm:px-7" style={{ background: "var(--pg-bg)", borderColor: "var(--pg-border)" }}>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="pg-btn-primary w-full disabled:cursor-not-allowed disabled:opacity-60"
                            data-testid="admin-project-submit"
                        >
                            {submitting ? "Saving…" : editing ? "Save Changes" : "Create & Generate Code"}
                            {!submitting && <ArrowRight className="h-4 w-4" strokeWidth={1.6} />}
                        </button>
                    </div>
                </form>
            </motion.aside>
        </motion.div>
    );
};

const DrawerLabel = ({ children }) => (
    <span className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "var(--pg-fg-muted)" }}>
        {children}
    </span>
);

const DrawerField = ({ id, label, value, onChange, required, placeholder, error, testId }) => (
    <div className="flex flex-col gap-1.5">
        <label htmlFor={id}>
            <DrawerLabel>
                {label}{required && <span style={{ color: "var(--pg-accent)" }}> *</span>}
            </DrawerLabel>
        </label>
        <input
            id={id}
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="pg-input"
            data-testid={testId}
        />
        {error && (
            <p className="flex items-center gap-1.5 text-[11px]" style={{ color: "#E55353" }}>
                <AlertCircle className="h-3 w-3" /> {error}
            </p>
        )}
    </div>
);
